<script lang="ts" setup>
import AuthLayout from '@/layouts/AuthLayout.vue'
import ReportFilter from '@/components/features/reports/ReportFilter.vue'
import ProductionByMachineReportDialog from '@/components/features/reports/ProductionByMachineReportDialog.vue'
import {getProductionByMachine} from '@/api/reports'
import {getBatchList} from '@/api/batches'
import {computed, onMounted, ref, watch} from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import Message from 'primevue/message'
import {useToast} from 'primevue/usetoast'

import type {ProductionByMachineReportParams, ProductionByMachineReportResponse} from '@/types/report-types'
import type {Batch as DomainBatch} from '@/types/batch-types'

const toast = useToast()

const selectedMachine = ref<string | null>(null)
const startDate = ref<Date | null>(null)
const endDate = ref<Date | null>(null)

const reportData = ref<ProductionByMachineReportResponse | null>(null)
const isLoading = ref(false)
const isLoadingMachines = ref(false)
const errorMessage = ref<string | null>(null)
const showReportDialog = ref(false)

const fetchedBatches = ref<DomainBatch[]>([])

const machineOptions = computed(() => {
  const options: { label: string; value: string }[] = []
  if (fetchedBatches.value.length > 0) {
    const machineNumbersFromBatches = fetchedBatches.value
        .map(batch => batch.machineNumber)
        .filter((mn): mn is string => typeof mn === 'string' && mn.trim() !== '')

    const uniqueMachines = [...new Set(machineNumbersFromBatches)]

    uniqueMachines.sort((a, b) => {
      const numA = parseInt(a, 10)
      const numB = parseInt(b, 10)
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB
      }
      return a.localeCompare(b)
    })

    uniqueMachines.forEach(machineNum => {
      options.push({ label: `${machineNum}`, value: machineNum })
    })
  }
  return options
})

watch(machineOptions, (newOptions) => {
  if (newOptions.length > 0 && selectedMachine.value === null) {
    selectedMachine.value = newOptions[0].value
  } else if (newOptions.length === 0) {
    selectedMachine.value = null
  }
}, { immediate: true })

onMounted(async () => {
  isLoadingMachines.value = true
  try {
    fetchedBatches.value = await getBatchList()
    if (machineOptions.value.length > 0 && selectedMachine.value === null) {
      selectedMachine.value = machineOptions.value[0].value
    }
  } catch (error: any) {
    console.error('Error fetching machine numbers (via batches):', error)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load machine options.', life: 3000 })
  } finally {
    isLoadingMachines.value = false
  }
})

const handleGenerateReport = async () => {
  if (!startDate.value || !endDate.value) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Please select both a start and end date.',
      life: 3000
    })
    return
  }
  if (!selectedMachine.value) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please select a machine number.', life: 3000 })
    return
  }

  isLoading.value = true
  errorMessage.value = null
  reportData.value = null

  try {
    const params: ProductionByMachineReportParams = {
      startDate: startDate.value.toISOString(),
      endDate: endDate.value.toISOString(),
      machineNumber: selectedMachine.value
    }

    const data = await getProductionByMachine(params)

    if (data && data.qualityProductions && data.qualityProductions.length > 0) {
      reportData.value = data
      showReportDialog.value = true
    } else {
      reportData.value = null
      toast.add({
        severity: 'info',
        summary: 'No Data',
        detail: 'No production data found for the selected criteria.',
        life: 4000
      })
      showReportDialog.value = false
    }

  } catch (error: any) {
    console.error('Error fetching Production by Machine report:', error)
    const apiErrorMessage = error.response?.data?.title || error.response?.data?.message || error.message || 'Failed to fetch report data.'
    errorMessage.value = apiErrorMessage
    toast.add({ severity: 'error', summary: 'API Error', detail: apiErrorMessage, life: 5000 })
    showReportDialog.value = false
  } finally {
    isLoading.value = false
  }
}

const handleCloseReportDialog = () => {
  showReportDialog.value = false
}
</script>

<template>
  <AuthLayout>
    <div class="min-h-screen p-4 md:p-6">
      <ReportFilter
          title="Production by Machine"
          dropdownLabel="Machine Number"
          v-model="selectedMachine"
          :dropdownOptions="machineOptions"
          :disabled="isLoadingMachines || machineOptions.length === 0"
          placeholder="Select a Machine"
          @update:startDate="startDate = $event"
          @update:endDate="endDate = $event"
          @generate-report="handleGenerateReport"
      />

      <div v-if="isLoading || isLoadingMachines" class="flex justify-center items-center mt-10">
        <ProgressSpinner strokeWidth="4" style="width: 50px; height: 50px" animationDuration=".5s" />
        <p class="ml-3 text-lg text-gray-600">
          {{ isLoadingMachines ? 'Loading machine options...' : 'Generating Report...' }}
        </p>
      </div>

      <div v-if="!isLoadingMachines && machineOptions.length === 0 && !isLoading"
           class="mt-6 text-center text-orange-600">
        <p>No machine numbers available to generate a report. Please ensure batches with machine numbers exist.</p>
      </div>

      <Message v-if="errorMessage && !showReportDialog" severity="error" :closable="true" class="mt-6">
        {{ errorMessage }}
      </Message>

      <div
          v-if="!isLoading && !isLoadingMachines && !reportData && !errorMessage && !showReportDialog && machineOptions.length > 0"
          class="mt-10 text-center text-gray-500">
        <p>Select your criteria and click "Create Report" to view details.</p>
      </div>

      <ProductionByMachineReportDialog
          :visible="showReportDialog"
          :reportData="reportData"
          :startDate="startDate"
          :endDate="endDate"
          :machineNumber="selectedMachine"
          :onClose="handleCloseReportDialog"
      />
    </div>
  </AuthLayout>
</template>
